import { useState, useEffect, useCallback } from 'react';
import { useSpeechRecognition } from './useSpeechRecognition';
import { useSettings } from '../contexts/settingsContext';

/**
 * Custom hook for managing speech recognition in event dialogs
 * Handles auto-start/stop based on focus and settings
 * @param {string} noteText - Current note text
 * @param {Function} setNoteText - Function to update note text
 * @param {Function} showSnackbar - Function to show snackbar messages
 * @param {boolean} open - Whether dialog is open
 * @returns {Object} Speech recognition state and handlers
 */
export function useEventSpeech(noteText, setNoteText, showSnackbar, open) {
  const [isNoteFocused, setIsNoteFocused] = useState(false);
  const [manuallyStoppedVoice, setManuallyStoppedVoice] = useState(false);

  const { autoEnableVoice } = useSettings();

  // Speech recognition - append recognized speech to note text
  const {
    isListening,
    isSupported: isSpeechSupported,
    startListening,
    stopListening
  } = useSpeechRecognition(
    false,
    (finalText) => {
      // Append recognized text to existing note
      setNoteText(prev => prev ? `${prev} ${finalText}` : finalText);
    },
    (error) => {
      console.error('[EventSpeech] Speech recognition error:', error);
      showSnackbar(`Speech recognition error: ${error}`, 'error');
    }
  );

  // Auto-start speech when note field is focused
  useEffect(() => {
    if (isNoteFocused && autoEnableVoice && isSpeechSupported && !isListening && !manuallyStoppedVoice) {
      startListening();
    } else if (!isNoteFocused && isListening) {
      stopListening();
    }
  }, [isNoteFocused, autoEnableVoice, isSpeechSupported, isListening, manuallyStoppedVoice, startListening, stopListening]);

  // Reset manual stop flag when field loses focus
  useEffect(() => {
    if (!isNoteFocused && !isListening) {
      setManuallyStoppedVoice(false);
    }
  }, [isNoteFocused, isListening]);

  // Stop listening when dialog closes
  useEffect(() => {
    if (!open && isListening) {
      stopListening();
    }
  }, [open, isListening, stopListening]);

  const handleStopListening = useCallback(() => {
    stopListening();
    setManuallyStoppedVoice(true);
  }, [stopListening]);

  return {
    isListening,
    isNoteFocused,
    setIsNoteFocused,
    handleStopListening
  };
}
